import useLoadChatMessages from '../../../elements/hooks/useLoadChatMessages';
import useInfo from '../../../hooks/useInfo';
import useStateRef from '../../../hooks/useStateRef';
import useSessionEvents from '../../../elements/hooks/useSessionEvents';
import EventHandler from '../../../services/EventHandler';
import usePromptsInfo from '../../prompts/hooks/usePrompts';
import { useState, useEffect, useCallback, useRef, useMemo, DependencyList } from 'react';
import { ChatMessageStreamingResponse, MessageUpdater } from '../../../elements/chat-window/ChatWindow.types';
import { isoDate } from '../../../helpers/dateHelpers';
import { delimit, sortWith } from '../../../helpers/listsHelpers';
import { BackendEvent, SessionEventTypes } from '../../../models/BackendEvents';
import { ChatInputModel } from '../elements/ui/chat-input/models';
import { ChatMessageFeedback } from '../types/chat.types';
import { ExtendedIChatService, useChat } from '../../../contexts/chat/ChatContext';
import { isString } from '../../../helpers/typeHelpers';
import { useAuthenticationHelper } from '../../../hooks/useAuthenticationHelper';

type PromptChangePayload = Readonly<{ message: string; key: string; }>;

const promptChangeEvent = new EventHandler<PromptChangePayload>();

export const useListenToPromptChange = (callback: (message: string, key: string) => void) => {
    useEffect(() => {
        const handler = ({ message, key }: PromptChangePayload) => {
            if (message) {
                callback(message, key);
            }
        };

        promptChangeEvent.on(handler);
        return () => {
            promptChangeEvent.off(handler);
        };
    }, [callback]);
};

export const invokePromptChangeEvent = (message: string, key: string) => {
    if (message) {
        promptChangeEvent.invoke({ message, key });
    }
};

const TEST_PERSISTENT_FALLBACK = { getInput: () => {}, removeInput: () => {}, mergeInput: () => {}, getAutoSubmit: () => {}, removeAutoSubmit: () => {}, };

export type UseMessagesStateReturnType = ReturnType<typeof useMessagesState>;

export type UseMessagesStateProps = {
    chatId: string;

    infoProps: ReturnType<typeof useInfo>;
    scrollToBottomWithDelay: () => void;
    scrollToBottomWithDependencies?: DependencyList;

    persistent: ExtendedIChatService['persistent'];
    prompt: ReturnType<typeof useChat>['prompt'];
    createChat?: ReturnType<typeof useChat>['createChat'];
    onNewChatCreated?: (id: string) => void;

    externalModelMetadata?: ChatInputModel;
    registerError?: (message: string) => void;

    reloadOnChatIdChange?: boolean;
};


const useMessagesState = ({ scrollToBottomWithDelay, prompt, createChat, onNewChatCreated, registerError, scrollToBottomWithDependencies, externalModelMetadata, infoProps, chatId, persistent, reloadOnChatIdChange }: UseMessagesStateProps) => {
    const externalAdminKey = useMemo(() => delimit([externalModelMetadata?.persona, externalModelMetadata?.llm, externalModelMetadata?.version, externalModelMetadata?.build], '-'), [externalModelMetadata]);
    const session_id = chatId;

    const { getInput, removeInput, mergeInput, getAutoSubmit, removeAutoSubmit } = persistent ?? TEST_PERSISTENT_FALLBACK;

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [feedbacks, setFeedbacks] = useState<ChatMessageFeedback[]>();
    const [messages, setMessages, messagesRef] = useStateRef<ChatMessageStreamingResponse[]>([]);

    const [inputModel, setInputModel, inputModelRef] = useStateRef(getInput(session_id, { message: '', ...externalModelMetadata }));
    const { isPromptTemplateCreatedByMe, getCurrentPromptTemplateId, addIntoTemporaryMessageStack } = usePromptTemplateIdState();

    const { chat, isLoadingChatHistory: isLoadingHistory, chatHistoryError, renderChatHistoryError: renderHistoryError, reload: reloadChat } = useLoadChatMessages({ chatId: session_id, active: !!session_id, onMessageResolve: setMessages, reloadOnChatIdChange });

    const { events, documents, setDocuments, setEvents } = useSessionEvents({ scrollToBottomWithDelay, session_id });

    // Update input model when chatId changes
    useEffect(() => {
        const session_id = chat?.session?.session_id;
        const id = session_id || externalAdminKey;
        if (id) setInputModel(getInput(id, { message: '', ...externalModelMetadata }));

        setEvents(chat?.events ?? []);
        setFeedbacks(chat?.feedback ?? []);
        setDocuments(chat?.session?.documents ?? []); // Initial documents
    }, [chat, setFeedbacks, setInputModel, getInput, setEvents, setDocuments, externalModelMetadata, externalAdminKey, session_id]);

    // Scroll to bottom when new messages are added
    useEffect(() => {
        scrollToBottomWithDelay();
    }, [session_id, scrollToBottomWithDependencies, scrollToBottomWithDelay]);


    const onInputModelMerge = useCallback((model: Partial<ChatInputModel>) => { setInputModel((m: any) => ({ ...m, ...model })); mergeInput(session_id || externalAdminKey, model); }, [session_id, externalAdminKey, mergeInput, setInputModel]);
    const onInputModelReset = useCallback(() => { setInputModel({}); removeInput(session_id); mergeInput(chat?.session.persona ?? '', { promptTemplateId: '' }); }, [setInputModel, removeInput, session_id, mergeInput, chat?.session.persona]);
    const updateMessageById = useCallback<MessageUpdater>((id, updates, addIfNotExist) => {
        setMessages((messages) => {
            let targetMessage = messages.find(x => x.id === id);

            if (!targetMessage) {
                if (!addIfNotExist) return messages;
                else {
                    targetMessage = { created: isoDate(undefined, true), ...updates, ...addIfNotExist, id };
                    messages = [...messages, targetMessage];
                }
            }

            const targetIndex = messages.indexOf(targetMessage);
            const updatedMessage = { ...targetMessage, ...updates, id: targetMessage.id };
            const updatedMessages = [...messages];

            updatedMessages.splice(targetIndex, 1, updatedMessage);
            return updatedMessages;
        });
    }, [setMessages]);

    const modelMetadata = useMemo(() => {
        const { llm, persona, persona_version: version, persona_build: build, productivity_gain: productivityGain } = chat?.session || {};

        return {
            ...inputModel,
            llm,
            persona: persona === null ? '' : persona, // This check is needed because persona can be null or undefined, in case of undefined it is not loaded yet
            version,
            build,
            productivityGain,
            documents,
        };
    }, [chat?.session, inputModel, documents]);

    const onSubmitPromptHandler = useCallback(async (sessionId: string, model: ChatInputModel) => {
        if (isLoadingHistory || chatHistoryError) return;
        const message = model.message;
        if (!message) return;

        const { response, abort, aiId, humanId } = await prompt(message, sessionId, model.promptTemplateId);

        updateMessageById(humanId!, {}, { type: 'human', content: message || '' });
        updateMessageById(aiId!, { isStreaming: true, stream: { response, abortResponse: abort } }, { type: 'ai', content: '' });
        scrollToBottomWithDelay();
        if (humanId && model.promptTemplateId) addIntoTemporaryMessageStack(humanId, model.promptTemplateId);
    }, [isLoadingHistory, chatHistoryError, prompt, updateMessageById, addIntoTemporaryMessageStack, scrollToBottomWithDelay]);

    let isPrompting = useRef(false);
    const onSendPromptHandler = useCallback(async () => {
        if (isPrompting.current) return;
        let sessionId = session_id;

        try {
            setIsSubmitting(true);
            isPrompting.current = true;

            if (!sessionId && !!externalModelMetadata) {
                // removeInput(externalAdminKey);

                const message = inputModelRef.current.message;
                const { build, llm, persona, temperature, version } = externalModelMetadata;
                if (!message || !llm || temperature === undefined) {
                    const requiredFields = [{ llm }, { temperature }, { message }];
                    let withoutValues = requiredFields.filter(x => {
                        const value = Object.values(x)[0];
                        return value === undefined || value === null || value === '';
                    }).map(x => Object.keys(x)[0]).join(', ');

                    if (withoutValues.includes("llm"))
                        withoutValues += ". Ensure you have selected a model under the Knowledge tab";
                    const error = `Cannot initiate prompt, due to missing following required fields: ${withoutValues}`;
                    throw new Error(error);
                }

                const { session_id } = await createChat?.(message, llm, temperature, persona, version, build) ?? {};
                session_id && (sessionId = session_id);
                onNewChatCreated?.(sessionId);
            }

            await onSubmitPromptHandler(sessionId, inputModelRef.current);
            setIsSubmitting(false);
            onInputModelReset();
        }
        catch (error) {
            setIsSubmitting(false);
            isPrompting.current = false;
            if (!registerError) {
                console.error(error);
                throw error;
            }
            else registerError(isString(error) ? error : (error as any)?.message || '');
        }

        isPrompting.current = false;
    }, [session_id, externalModelMetadata, onSubmitPromptHandler, inputModelRef, onInputModelReset, registerError, createChat, onNewChatCreated]);

    const isAutoSubmitRequiredRef = useRef(getAutoSubmit(session_id));
    const [isAutoSubmitting, setIsAutoSubmitting] = useState(!!isAutoSubmitRequiredRef.current);

    useEffect(() => {
        if (!isAutoSubmitRequiredRef.current) return;
        if (isLoadingHistory) return;
        removeAutoSubmit();
        isAutoSubmitRequiredRef.current = false;
        onSendPromptHandler().finally(() => setIsAutoSubmitting(false));
    }, [onSendPromptHandler, removeAutoSubmit, isLoadingHistory]);

    const isStreaming = useMemo(() => messages?.some(x => !!x.isStreaming) ?? false, [messages]);

    const combinedMessages = useMemo(() => ([...messages, ...events]), [messages, events]);
    const sortedCombinedMessages = useMemo(() => sortWith(combinedMessages, (item) => 'timestamp' in item ? item.timestamp : item?.created), [combinedMessages]);

    const groupedEventMessages = useMemo(() => {
        const messages = [];
        let temporaryPairs: ({ message: BackendEvent; } & { q: number; })[] = [];
        let prevEventType = '';

        sortedCombinedMessages.forEach((message) => {
            if ('event_id' in message) {
                if (message.type !== prevEventType) {
                    messages.push(temporaryPairs);
                    temporaryPairs = [];
                    prevEventType = message.type;
                    temporaryPairs.push({ message, q: 1 });
                }
                else {
                    temporaryPairs.push({ message, q: temporaryPairs.length + 1 });
                }
            }

            else {
                if (temporaryPairs.length > 0) {
                    messages.push(temporaryPairs);
                    temporaryPairs = [];
                }

                messages.push(message);
            }
        });

        if (temporaryPairs.length > 0) {
            messages.push(temporaryPairs);
            temporaryPairs = [];
        }

        return messages as unknown as (ChatMessageStreamingResponse | BackendEvent)[];
    }, [sortedCombinedMessages]);

    const groupedAndSortedMessages = useMemo(() => {
        return groupedEventMessages.reduce((acc, curr, index) => {
            if (Array.isArray(curr)) {
                const current = curr;
                const lastElement = current?.[current?.length - 1];
                if (!lastElement) return acc;

                let quantity = current.length;

                // Deleted document event should show the quantity of documents removed
                if (lastElement?.message?.type === SessionEventTypes.documentRemoved) {
                    quantity = current.reduce((acc, curr) => acc + curr?.message?.metadata?.documents?.length, 0);
                }
                const groupedEvent = { ...lastElement.message, quantity };

                return [...acc, groupedEvent];
            }


            const humanId = 'id' in curr ? curr.id : undefined;

            return [...acc, {
                ...curr,
                index,
                isPromptTemplateCreatedByMe: isPromptTemplateCreatedByMe(humanId),
                promptTemplateId: getCurrentPromptTemplateId(humanId), //
            }];
        }, [] as (ChatMessageStreamingResponse | BackendEvent)[]);
    }, [getCurrentPromptTemplateId, groupedEventMessages, isPromptTemplateCreatedByMe]);

    const resetChat = useCallback(() => {
        setMessages([]);
        setFeedbacks([]);
        setEvents([]);
        setDocuments([]);
        setInputModel({ message: '', ...externalModelMetadata });
        onInputModelReset();
    }, [setMessages, setEvents, setDocuments, setInputModel, externalModelMetadata, onInputModelReset]);


    return {
        chat: {
            chat,
            chatId,
            infoProps,
            modelMetadata,
            feedbacks,
            messagesRef,
            messages: groupedAndSortedMessages,
            isSubmitting,
            isStreaming,
            isLoadingHistory,
            isAutoSubmitting,

            chatHistoryError,
            renderHistoryError,

            onInputModelMerge,
            onSendPromptHandler,
            setMessages: updateMessageById,

            resetChat,
            reloadChat
        },
    };
};

export default useMessagesState;



// Purpose to sore and access one time information .... such as promptTemplateId if it was used in the chat at that moment
const usePromptTemplateIdState = () => {
    const [temporaryMessageStack, setTemporaryMessageStack] = useState<Record<string, string>>({});
    const { checkIfCreatedByMe } = useAuthenticationHelper();
    const { getPromptByKey } = usePromptsInfo();

    const getCurrentPromptTemplateId = useCallback((id?: string) => id && temporaryMessageStack[id] ? temporaryMessageStack[id] : undefined, [temporaryMessageStack]);
    const getCurrentPrompt = useCallback((id?: string) => id && getCurrentPromptTemplateId(id) ? getPromptByKey(getCurrentPromptTemplateId(id)!) : undefined, [getCurrentPromptTemplateId, getPromptByKey]);
    const isPromptTemplateCreatedByMe = useCallback((id?: string) => id && getCurrentPrompt(id)?.created_by ? checkIfCreatedByMe(getCurrentPrompt(id)?.created_by) : false, [getCurrentPrompt, checkIfCreatedByMe]);
    const addIntoTemporaryMessageStack = useCallback((id: string, promptTemplateId: string) => id && setTemporaryMessageStack((prev) => ({ ...prev, [id]: promptTemplateId })), [setTemporaryMessageStack]);

    return {
        getCurrentPromptTemplateId,
        addIntoTemporaryMessageStack,
        isPromptTemplateCreatedByMe
    };
};
